import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Clock } from 'lucide-react';

const TIMEOUT_MS = 30 * 60 * 1000;       // 30 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000;  // warn 5 min before
const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export default function SessionTimeoutManager() {
  const { logout, token } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);

  const resetTimers = useCallback(() => {
    if (!token) return;
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);
    setShowWarning(false);

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemaining(Math.floor(WARNING_BEFORE_MS / 1000));
      countdownRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }, TIMEOUT_MS - WARNING_BEFORE_MS);

    timerRef.current = setTimeout(() => {
      clearInterval(countdownRef.current);
      logout();
    }, TIMEOUT_MS);
  }, [token, logout]);

  useEffect(() => {
    if (!token) return;
    resetTimers();
    const handler = () => { if (!showWarning) resetTimers(); };
    EVENTS.forEach(e => document.addEventListener(e, handler, { passive: true }));
    return () => {
      EVENTS.forEach(e => document.removeEventListener(e, handler));
      clearTimeout(timerRef.current);
      clearTimeout(warningRef.current);
      clearInterval(countdownRef.current);
    };
  }, [token, resetTimers, showWarning]);

  const handleStayLoggedIn = () => {
    resetTimers();
  };

  if (!token || !showWarning) return null;

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <Clock className="h-5 w-5" /> Session Expiring
          </DialogTitle>
          <DialogDescription>
            Your session will expire in{' '}
            <span className="font-bold text-amber-700">{mins}:{secs.toString().padStart(2, '0')}</span>{' '}
            due to inactivity.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { logout(); }} data-testid="session-timeout-logout-btn">
            Log Out Now
          </Button>
          <Button onClick={handleStayLoggedIn} data-testid="session-timeout-stay-btn">
            Stay Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
